import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { loc } from "../lib/localize";
import { sideMultipliers } from "../lib/odds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Loader2, ImagePlus, X, ThumbsUp, ThumbsDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function sideColorCls(side) {
  return side === "agree" ? "text-lie" : "text-truth";
}

const EMPTY = {
  title_nl: "", title_en: "",
  summary_nl: "", summary_en: "",
  claim_nl: "", claim_en: "",
  body_nl: "", body_en: "",
  category_nl: "Algemeen", category_en: "General",
  youtube_url: "",
  synonyms_nl: "", synonyms_en: "",
  probability: 50,
  image_path: null, image_url: null,
};

export default function AdminDashboard() {
  const { t, translateError, lang } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/articles").then(({ data }) => setArticles(data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      title_nl: a.title_nl, title_en: a.title_en,
      summary_nl: a.summary_nl, summary_en: a.summary_en,
      claim_nl: a.claim_nl || "", claim_en: a.claim_en || "",
      body_nl: a.body_nl, body_en: a.body_en,
      category_nl: a.category_nl, category_en: a.category_en,
      youtube_url: a.youtube_url || "",
      synonyms_nl: (a.synonyms_nl || []).join(", "),
      synonyms_en: (a.synonyms_en || []).join(", "),
      probability: a.probability,
      image_path: a.image_path || null, image_url: a.image_url || null,
    });
    setOpen(true);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: data.path, image_url: data.url }));
      toast.success(t("admin.imageUploaded"));
    } catch (err) {
      toast.error(translateError(formatApiError(err.response?.data?.detail)));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title_nl.trim() || !form.title_en.trim() || !form.body_nl.trim() || !form.body_en.trim()) {
      return toast.error(t("admin.reqBilingual"));
    }
    setSaving(true);
    const payload = {
      ...form,
      synonyms_nl: form.synonyms_nl.split(",").map((s) => s.trim()).filter(Boolean),
      synonyms_en: form.synonyms_en.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/articles/${editing.id}`, payload);
        toast.success(t("admin.updated"));
      } else {
        await api.post("/articles", payload);
        toast.success(t("admin.created"));
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(translateError(formatApiError(e.response?.data?.detail)));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/articles/${a.id}`);
      toast.success(t("admin.deleted"));
      load();
    } catch (e) {
      toast.error(translateError(formatApiError(e.response?.data?.detail)));
    }
  };

  const sm = sideMultipliers(form.probability);

  const langFields = (code) => (
    <div className="space-y-5">
      <div>
        <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("admin.fieldTitle")}</Label>
        <Input data-testid={`article-title-${code}`} value={form[`title_${code}`]} onChange={(e) => set(`title_${code}`, e.target.value)} className="border-white/15 bg-background" placeholder={t("admin.titlePlaceholder")} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("admin.fieldCategory")}</Label>
          <Input value={form[`category_${code}`]} onChange={(e) => set(`category_${code}`, e.target.value)} className="border-white/15 bg-background" />
        </div>
        <div>
          <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("admin.fieldSummary")}</Label>
          <Input value={form[`summary_${code}`]} onChange={(e) => set(`summary_${code}`, e.target.value)} className="border-white/15 bg-background" placeholder={t("admin.summaryPlaceholder")} />
        </div>
      </div>
      <div>
        <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("admin.fieldClaim")}</Label>
        <Textarea data-testid={`article-claim-${code}`} value={form[`claim_${code}`]} onChange={(e) => set(`claim_${code}`, e.target.value)} rows={2} className="border-white/15 bg-background" placeholder={t("admin.claimPlaceholder")} />
        <p className="mt-2 text-xs text-muted-foreground">{t("admin.claimHint")}</p>
      </div>
      <div>
        <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("admin.fieldBody")}</Label>
        <Textarea data-testid={`article-body-${code}`} value={form[`body_${code}`]} onChange={(e) => set(`body_${code}`, e.target.value)} rows={8} className="border-white/15 bg-background" />
      </div>
      <div>
        <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("admin.fieldSynonyms")}</Label>
        <Input data-testid={`article-synonyms-${code}`} value={form[`synonyms_${code}`]} onChange={(e) => set(`synonyms_${code}`, e.target.value)} className="border-white/15 bg-background" placeholder={t("admin.synonymsPlaceholder")} />
        <p className="mt-2 text-xs text-muted-foreground">{t("admin.synonymsHint")}</p>
      </div>
    </div>
  );

  return (
    <main className="relative z-10 mx-auto max-w-6xl px-6 py-14 md:px-10">
      <div className="mb-10 flex items-end justify-between">
        <div>
          <span className="font-mono text-xs uppercase tracking-widest text-primary">{t("admin.eyebrow")}</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter sm:text-5xl">{t("admin.title")}</h1>
          <p className="mt-3 text-muted-foreground">{t("admin.published", { n: articles.length })}</p>
        </div>
        <Button onClick={openCreate} data-testid="new-article-btn" className="rounded-md bg-primary font-bold uppercase tracking-wider hover:bg-[#FF645A]">
          <Plus className="mr-2 h-4 w-4" /> {t("admin.newArticle")}
        </Button>
      </div>

      <div className="overflow-hidden border border-white/10">
        <table className="w-full text-sm" data-testid="admin-articles-table">
          <thead className="border-b border-white/10 bg-card">
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <th className="p-4">{t("admin.colTitle")}</th>
              <th className="p-4">{t("admin.colCategory")}</th>
              <th className="p-4 text-right">{t("admin.colProb")}</th>
              <th className="p-4 text-right">{t("admin.colFactor")}</th>
              <th className="p-4 text-right">{t("admin.colBets")}</th>
              <th className="p-4 text-right">{t("admin.colActions")}</th>
            </tr>
          </thead>
          <tbody>
            {articles.map((a) => (
              <tr key={a.id} className="border-b border-white/5 hover:bg-card" data-testid="admin-article-row">
                <td className="p-4 font-medium">{loc(a, "title", lang)}</td>
                <td className="p-4 text-muted-foreground">{loc(a, "category", lang)}</td>
                <td className="p-4 text-right font-mono">{a.probability}%</td>
                <td className={`p-4 text-right font-heading font-bold ${sideColorCls(a.multiplier_side)}`}>{a.headline_multiplier}× <span className="font-mono text-[10px] uppercase">{a.multiplier_side === "agree" ? t("article.agree") : t("article.disagree")}</span></td>
                <td className="p-4 text-right font-mono">{a.bet_count}</td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    <Link to={`/wiki/${a.slug}`} className="p-2 text-muted-foreground hover:text-foreground"><ExternalLink className="h-4 w-4" /></Link>
                    <button onClick={() => openEdit(a)} data-testid={`edit-${a.slug}`} className="p-2 text-muted-foreground hover:text-credit"><Pencil className="h-4 w-4" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`delete-${a.slug}`} className="p-2 text-muted-foreground hover:text-lie"><Trash2 className="h-4 w-4" /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="border-white/10 bg-card">
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("admin.deleteTitle")}</AlertDialogTitle>
                          <AlertDialogDescription>{t("admin.deleteDesc", { title: loc(a, "title", lang) })}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="border-white/15 bg-transparent">{t("common.cancel")}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(a)} className="bg-lie hover:bg-lie/80">{t("admin.delete")}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
            {articles.length === 0 && (
              <tr><td colSpan={6} className="p-12 text-center text-muted-foreground">{t("admin.empty")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-card">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black">{editing ? t("admin.editTitle") : t("admin.createTitle")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div>
              <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("admin.image")}</Label>
              {form.image_url ? (
                <div className="relative w-full overflow-hidden border border-white/10">
                  <img src={`${BACKEND_URL}${form.image_url}`} alt="" className="max-h-52 w-full object-cover" data-testid="editor-image-preview" />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, image_path: null, image_url: null }))}
                    data-testid="remove-image-btn"
                    className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center bg-[#0A0A0A]/80 text-white hover:bg-lie"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="flex cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-white/20 bg-background py-8 text-muted-foreground transition-colors hover:border-white/40 hover:text-foreground" data-testid="image-upload-label">
                  {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
                  <span className="text-sm">{uploading ? t("admin.uploading") : t("admin.uploadHint")}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} data-testid="image-upload-input" />
                </label>
              )}
            </div>

            <Tabs defaultValue="nl">
              <TabsList className="grid w-full grid-cols-2 bg-background">
                <TabsTrigger value="nl" data-testid="tab-nl">🇳🇱 Nederlands</TabsTrigger>
                <TabsTrigger value="en" data-testid="tab-en">🇬🇧 English</TabsTrigger>
              </TabsList>
              <TabsContent value="nl" className="mt-5">{langFields("nl")}</TabsContent>
              <TabsContent value="en" className="mt-5">{langFields("en")}</TabsContent>
            </Tabs>

            <div>
              <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("admin.fieldVideo")}</Label>
              <Input
                data-testid="article-youtube"
                value={form.youtube_url}
                onChange={(e) => set("youtube_url", e.target.value)}
                className="border-white/15 bg-background"
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="mt-2 text-xs text-muted-foreground">{t("admin.videoHint")}</p>
            </div>

            <div className="border border-white/10 bg-background p-5">
              <div className="mb-4 flex items-center justify-between">
                <Label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("admin.probability", { p: form.probability })}</Label>
                <div className="flex items-center gap-4" data-testid="admin-multiplier-preview">
                  <span className="flex items-center gap-1.5 font-heading text-xl font-black text-lie">
                    <ThumbsUp className="h-4 w-4" /> {sm.agree}×
                  </span>
                  <span className="flex items-center gap-1.5 font-heading text-xl font-black text-truth">
                    <ThumbsDown className="h-4 w-4" /> {sm.disagree}×
                  </span>
                </div>
              </div>
              <Slider
                data-testid="probability-slider"
                min={1} max={99} step={1}
                value={[form.probability]}
                onValueChange={(v) => set("probability", v[0])}
              />
              <p className="mt-3 text-xs text-muted-foreground">{t("admin.probHint")}</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/15 bg-transparent">{t("common.cancel")}</Button>
            <Button onClick={save} disabled={saving} data-testid="save-article-btn" className="rounded-md bg-primary font-bold uppercase tracking-wider hover:bg-[#FF645A]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? t("common.save") : t("admin.publish")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
